import { find } from "lodash";
import {
  BoardIds,
  FilterIdentifier,
  FilterUserSelection,
} from "../../common/generic/generic-filters-boards-attributes";
import {
  DataStatus,
  GenericAggregateResponse,
  IFilterItem,
  IGenericHyperlinkFilter,
  IHyperlinkFilterTypes,
} from "./generic-types";

function BuildValues(values: any[] | any) {
  if (values.length === 1) {
    return ReplaceInvalidCharacters(values[0]);
  }
  return ReplaceInvalidCharacters(values.join("+"));
}

// replaces , inside values with ~,
function ReplaceInvalidCharacters(valueString: string) {
  const re = /\,/gi;
  return valueString.replace(re, "~,");
}

export function AddFpvBlFilter(
  filters: IFilterItem[],
  fpvBlAttr: string,
  fpvBlValue?: string[]
) {
  if (fpvBlValue && fpvBlValue.length !== 0) {
    filters.push({
      attribute: fpvBlAttr,
      equals: true,
      value: BuildValues(fpvBlValue),
    });
  }
}

const addSpecificFiltersToAdvancedFilter = (
  specificFilterSelected: IFilterItem,
  filterRequest: string
): string => {
  const filterWithOperator =
    filterRequest.substring(0, 2) === "OR" ||
    filterRequest.substring(0, 3) === "AND";

  const lastChar = filterRequest.charAt(
    filterWithOperator ? filterRequest.length - 1 : filterRequest.length
  );

  if (filterRequest.substring(0, 2) === "OR") {
    const transformedLogicalFilter = `AND[${filterRequest},AND[${specificFilterSelected.attribute}:eq:${specificFilterSelected.value}]]`;
    return transformedLogicalFilter;
  }

  const transformedLogicalFilter = filterRequest
    .substring(
      0,
      filterWithOperator ? filterRequest.length - 1 : filterRequest.length
    )
    .concat(
      `,${specificFilterSelected.attribute}:eq:${specificFilterSelected.value}`
    )
    .concat(lastChar);

  return transformedLogicalFilter;
};

// tslint:disable-next-line: parameters-max-number
export function BuildGenericRequest(
  kpis: string,
  date?: Date,
  attributes?: string,
  topN?: number,
  page?: number,
  size?: number,
  filter?: any,
  sort?: string,
  topNSort?: string
) {
  const useDate = SetDate(date);
  const concatedfilter = GenericFilterBuilder(filter);
  return {
    aggregationAttributes: attributes,
    date: useDate,
    filter: concatedfilter,
    kpis,
    page,
    size,
    sort,
    topN,
    topNSort,
  };
}

export function GenericFilterBuilder(filters: IFilterItem[]) {
  if (filters === undefined) {
    return "";
  }
  let filterRequest = "";

  const advancedFilterSelected = find(filters, {
    attribute: "logicalAttributesFilter",
  });
  const markscStepGroupFilterSelected = find(filters, {
    attribute: "marksc_stepgroup",
  });
  const voiceElecFilterSelected = find(filters, {
    attribute: "marksc_iselec",
  });

  if (advancedFilterSelected && advancedFilterSelected.value !== "") {
    filterRequest = advancedFilterSelected.value;

    for(const intrafilter of filters) {
      if(intrafilter.attribute === "logicalAttributesFilter") {
        continue;
      }
      else {
        filterRequest = addSpecificFiltersToAdvancedFilter(
          intrafilter,
          filterRequest
        );
      }
    }
    return filterRequest;
  } else {
    for (const intrafilter of filters) {
      if (intrafilter.equals) {
        filterRequest = `${filterRequest}${intrafilter.attribute}:${intrafilter.value},`;
      } else {
        filterRequest = `${filterRequest}-${intrafilter.attribute}:${intrafilter.value},`;
      }
    }

    return filterRequest.replace(/,\s*$/, "");
  }
}

function SetDate(date?: Date) {
  if (date !== undefined) {
    const dateReturn = date.toString();
    if (dateReturn.length > 10) {
      return date.toISOString().slice(0, 10);
    }
    return dateReturn;
  } else {
    return new Date().toISOString().slice(0, 10);
  }
}

export function BuildUrlForHyperlinkInGrid(params: IGenericHyperlinkFilter) {
  let url: string;
  const envString = "https://SG-dashboard-uat.fr.world.socgen";
  let env = "";
  if (envString.lastIndexOf("%#") !== -1) {
    env = "https://sg-dashboard-uat.fr.world.socgen";
  } else {
    env = envString;
  }

  const sgDashboardPathPrefix = getSgDashboardPathPrefix();
  const boardID = params.board_id;
  const configuration = GetBtoaConfiguration(params.board_id, params.filter);
  url = `${env}/${sgDashboardPathPrefix}/boards/${boardID}/preview?configuration=${configuration}`;
  return url;
}

export function BuildClient360Url(clientBdrCommercialId: string) {
  return `https://digital.fr.world.socgen/cli-projects/client360Views/index.html?bdrId=${clientBdrCommercialId}&bdrType=clientComm&view=homepage`
}

function getSgDashboardPathPrefix() {
  let sgDashboardPathPrefix = "#";
  // if the current url does not contains a hashtag, it means we are on the first version of the sg-dashboard
  if (window.location.href.indexOf("#") === -1) {
    // if the current url does not contains v2 identifier, it means that the v2 is not yet the default version
    if (window.location.href.indexOf("/v2/") === -1) {
      sgDashboardPathPrefix = "explore";
    } else {
      sgDashboardPathPrefix = "v2/explore";
    }
  } else {
    // if the current url contains v1 identifier, it means that the v1 is not the default version anymore
    if (window.location.href.indexOf("/v1/") > 0) {
      sgDashboardPathPrefix = "v1/#";
    }
  }
  return sgDashboardPathPrefix;
}

function GetBtoaConfiguration(
  boardId: string,
  filterType: IHyperlinkFilterTypes[]
) {
  let selector = "";
  filterType.forEach(element => {
    let keyValPair = "";
    element.value.forEach(val => {
      keyValPair += GetKeyValFormat(element.id, val, boardId);
    });
    selector += GetSelectorFormat(element.filter_id, keyValPair) + ",";
  });
  selector = selector.substring(0, selector.lastIndexOf(","));
  return btoa(`{"selectors":{${selector}}}`);
}

function GetKeyValFormat(key: any, value: any, boardId: string) {
  if (
    (boardId === BoardIds.Client_Snapshot ||
      boardId === BoardIds.Client_Card ||
      boardId === BoardIds.Portfolio_DealExport) &&
    key === FilterUserSelection.client_selector
  ) {
    return `{"${key}":["${value}"],"selectedSearchBy":"commercial"}`;
  } else if (
    boardId === BoardIds.Portfolio_DealExport &&
    key.startsWith(FilterUserSelection.fpv_bls)
  ) {
    return `{"${key}":["${value}"]}`;
  } else {
    return `{"${key}":"${value}"}`;
  }
}

function GetSelectorFormat(selectors: any, keyValPairs: any) {
  if (
    [
      FilterIdentifier.fpv_bl,
      FilterIdentifier.fpv_sbl,
      FilterIdentifier.fpv_ssbl,
    ].indexOf(selectors) !== -1
  ) {
    return `"${selectors}":{"inputs":${keyValPairs},"outputs":${keyValPairs}}`;
  } else {
    return `"${selectors}":{"inputs":${keyValPairs}}`;
  }
}

export function timeout(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function isNotNullOrEmpty(array: any[]) {
  return array ? array.length > 0 : false;
}

export function isUnauthorizedError(apiResponse: Response) {
  return (
    apiResponse.status === 401 && apiResponse.statusText === "Unauthorized"
  );
}

export function isForbiddenError(apiResponse: Response) {
  return apiResponse.status === 403 && apiResponse.statusText === "Forbidden";
}

export function isNotFoundError(apiResponse: Response) {
  return apiResponse.status === 404;
}

export function isInternalServerError(apiResponse: Response) {
  return apiResponse.status === 500;
}

export function isPreconditionFailedError(apiResponse: Response) {
  return apiResponse.status === 412;
}
export function HandleCsvCallErrors(
  responseData: GenericAggregateResponse
): string | null {
  if (responseData.noAccessRights) {
    return "no-access-rights";
  } else if (responseData.noClientMapping) {
    return "no-client-mapping";
  } else if (responseData.serverError) {
    return "server-error";
  }
  return null;
}
export function CheckReponseDataForError(
  apiResponse: GenericAggregateResponse
): DataStatus | undefined {
  if (apiResponse.noAccessRights === true) {
    return "no-access-rights";
  }
  if (apiResponse.noClientMapping === true) {
    return "no-client-mapping";
  }
  if (apiResponse.serverError === true) {
    return "server-error";
  }
  if (apiResponse.clientRequestError === true) {
    return "client-request-error";
  }
  if (
    apiResponse === undefined ||
    apiResponse.aggregates === undefined ||
    apiResponse.aggregates.length === 0
  ) {
    return "no-data";
  }
}
